import { uids } from '../constants/uids'

/**
 * Clasificación clínica/práctica del Transfer Syntax UID (0002,0010).
 * @typedef {'uncompressed'|'lossless'|'near-lossless'|'lossy'|'video'|'unknown'} TsKind
 */

/** @type {Record<string, { kind: TsKind, short: string }>} */
const TS_META = {
  '1.2.840.10008.1.2': { kind: 'uncompressed', short: 'Implicit LE' },
  '1.2.840.10008.1.2.1': { kind: 'uncompressed', short: 'Explicit LE' },
  '1.2.840.10008.1.2.1.99': { kind: 'lossless', short: 'Deflated Explicit LE' },
  '1.2.840.10008.1.2.2': { kind: 'uncompressed', short: 'Explicit BE (Retired)' },
  '1.2.840.10008.1.2.4.50': { kind: 'lossy', short: 'JPEG Baseline' },
  '1.2.840.10008.1.2.4.51': { kind: 'lossy', short: 'JPEG Extended' },
  '1.2.840.10008.1.2.4.52': { kind: 'lossy', short: 'JPEG Extended (Retired)' },
  '1.2.840.10008.1.2.4.53': { kind: 'lossy', short: 'JPEG Spectral (Retired)' },
  '1.2.840.10008.1.2.4.54': { kind: 'lossy', short: 'JPEG Spectral (Retired)' },
  '1.2.840.10008.1.2.4.55': { kind: 'lossy', short: 'JPEG Full Prog. (Retired)' },
  '1.2.840.10008.1.2.4.56': { kind: 'lossy', short: 'JPEG Full Prog. (Retired)' },
  '1.2.840.10008.1.2.4.57': { kind: 'lossless', short: 'JPEG Lossless (P14)' },
  '1.2.840.10008.1.2.4.58': { kind: 'lossless', short: 'JPEG Lossless (Retired)' },
  '1.2.840.10008.1.2.4.59': { kind: 'lossy', short: 'JPEG Hierarchical (Retired)' },
  '1.2.840.10008.1.2.4.60': { kind: 'lossy', short: 'JPEG Hierarchical (Retired)' },
  '1.2.840.10008.1.2.4.61': { kind: 'lossy', short: 'JPEG Hierarchical (Retired)' },
  '1.2.840.10008.1.2.4.62': { kind: 'lossy', short: 'JPEG Hierarchical (Retired)' },
  '1.2.840.10008.1.2.4.63': { kind: 'lossy', short: 'JPEG Hierarchical (Retired)' },
  '1.2.840.10008.1.2.4.64': { kind: 'lossy', short: 'JPEG Hierarchical (Retired)' },
  '1.2.840.10008.1.2.4.65': { kind: 'lossless', short: 'JPEG Lossless Hier. (Retired)' },
  '1.2.840.10008.1.2.4.66': { kind: 'lossless', short: 'JPEG Lossless Hier. (Retired)' },
  '1.2.840.10008.1.2.4.70': { kind: 'lossless', short: 'JPEG Lossless (SV1)' },
  '1.2.840.10008.1.2.4.80': { kind: 'lossless', short: 'JPEG-LS Lossless' },
  '1.2.840.10008.1.2.4.81': { kind: 'near-lossless', short: 'JPEG-LS Near-Lossless' },
  '1.2.840.10008.1.2.4.90': { kind: 'lossless', short: 'JPEG 2000 Lossless' },
  '1.2.840.10008.1.2.4.91': { kind: 'lossy', short: 'JPEG 2000' },
  '1.2.840.10008.1.2.4.92': { kind: 'lossless', short: 'JPEG 2000 MC Lossless' },
  '1.2.840.10008.1.2.4.93': { kind: 'lossy', short: 'JPEG 2000 MC' },
  '1.2.840.10008.1.2.4.94': { kind: 'lossy', short: 'JPIP Referenced' },
  '1.2.840.10008.1.2.4.95': { kind: 'lossy', short: 'JPIP Deflate' },
  '1.2.840.10008.1.2.4.100': { kind: 'video', short: 'MPEG-2 MP@ML' },
  '1.2.840.10008.1.2.4.101': { kind: 'video', short: 'MPEG-2 MP@HL' },
  '1.2.840.10008.1.2.4.102': { kind: 'video', short: 'MPEG-4 AVC/H.264' },
  '1.2.840.10008.1.2.4.103': { kind: 'video', short: 'MPEG-4 AVC BD' },
  '1.2.840.10008.1.2.4.104': { kind: 'video', short: 'MPEG-4 AVC 2D' },
  '1.2.840.10008.1.2.4.105': { kind: 'video', short: 'MPEG-4 AVC 3D' },
  '1.2.840.10008.1.2.4.106': { kind: 'video', short: 'MPEG-4 AVC Stereo' },
  '1.2.840.10008.1.2.4.107': { kind: 'video', short: 'HEVC/H.265 Main' },
  '1.2.840.10008.1.2.4.108': { kind: 'video', short: 'HEVC/H.265 Main 10' },
  '1.2.840.10008.1.2.5': { kind: 'lossless', short: 'RLE Lossless' },
}

const KIND_LABEL = {
  uncompressed: 'Uncompressed',
  lossless: 'Lossless',
  'near-lossless': 'Near-Lossless',
  lossy: 'Lossy',
  video: 'Video (lossy)',
  unknown: 'Desconocido',
}

const KIND_COLOR = {
  uncompressed: '#81C784',
  lossless: '#4DD0E1',
  'near-lossless': '#FFD54F',
  lossy: '#FF8A65',
  video: '#CE93D8',
  unknown: 'rgba(255,255,255,0.55)',
}

function guessKindFromName(name) {
  const n = String(name || '').toLowerCase()
  if (!n) return 'unknown'
  if (/\bnear[- ]?lossless\b/.test(n)) return 'near-lossless'
  if (/\blosseless\b/.test(n)) return 'lossless'
  if (/\blossy\b/.test(n)) return 'lossy'
  if (/mpeg|h\.?264|h\.?265|hevc|avc/.test(n)) return 'video'
  if (/little endian|big endian/.test(n) && !/jpeg|rle|deflate/.test(n)) {
    return 'uncompressed'
  }
  if (/deflated/.test(n)) return 'lossless'
  if (/rle/.test(n)) return 'lossless'
  return 'unknown'
}

/**
 * @param {string} uid
 * @returns {{
 *   uid: string,
 *   name: string,
 *   short: string,
 *   kind: TsKind,
 *   kindLabel: string,
 *   kindColor: string,
 *   summary: string,
 *   detail: string,
 * }}
 */
export function resolveTransferSyntax(uid) {
  const clean = String(uid || '')
    .trim()
    .replace(/\0/g, '')
  if (!clean) {
    return {
      uid: '',
      name: '',
      short: '',
      kind: 'unknown',
      kindLabel: KIND_LABEL.unknown,
      kindColor: KIND_COLOR.unknown,
      summary: 'Transfer Syntax no disponible',
      detail: '',
    }
  }

  const name = uids[clean] || ''
  const meta = TS_META[clean]
  const kind = meta ? meta.kind : guessKindFromName(name)
  const short = meta ? meta.short : name || clean
  const kindLabel = KIND_LABEL[kind] || KIND_LABEL.unknown
  const kindColor = KIND_COLOR[kind] || KIND_COLOR.unknown

  return {
    uid: clean,
    name: name || 'UID no catalogado',
    short,
    kind,
    kindLabel,
    kindColor,
    summary: `${kindLabel} · ${short}`,
    detail: name || clean,
  }
}

/**
 * Lee (0002,0010) de un DataSet / image.data.
 * @param {object} dataSet
 */
export function readTransferSyntaxFromDataSet(dataSet) {
  if (!dataSet || typeof dataSet.string !== 'function') return resolveTransferSyntax('')
  try {
    return resolveTransferSyntax(dataSet.string('x00020010'))
  } catch (e) {
    return resolveTransferSyntax('')
  }
}

export { KIND_LABEL, KIND_COLOR }
